import React  from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { quoteFormSelector } from 'ctm-selectors';
import { isHealthCrossellExperimentEnabled } from '../feature_flags_utils';
import { parseBrowser } from 'ctm-util-functions';
import styles from './cross_sell_widget.css';
import { SimpleFlexbox } from '../flexbox/flexbox';
import { Badge, Checkbox } from '../form';
import { Text } from '../text/text';

/**
 *
 * @param {Object} props - the component's props
 * @param {String} props.title - the widget title
 * @param {String} props.badgeLabel - the label text underneath the badge
 * @param {String} props.icon - the icon variant to use
 * @param {String} props.content - the SpeechBubble content
 * @param {String} props.value - the value of the check box from state
 * @param {Function} props.dispatch - dispatch function from redux
 * @returns {Node} A Cross cell widget component
 * @constructor
 */
// Hardcoded to 'leadCapture.health' because all variants (default, B, C) must read/write
// to the same Redux state path. The schema uses backendIdentifier to control the backend
// payload key, so this component intentionally manages state at a fixed path.
const HEALTH_LEAD_CAPTURE_ID = 'leadCapture.health';

const _CrossSellWidget = (props) => {
    const { Agent } = parseBrowser(window.navigator.userAgent);
    const { icon, badgeLabel, value, title, variant, dispatch, content, serverConfig } = props;
    const experimentEnabled = isHealthCrossellExperimentEnabled(serverConfig);

    // no variant (default): desktop only
    if (!variant && Agent.isMobile) return null;
    // variants B and C are gated by the healthCrossellExperimentEnabled feature flag
    if (variant && !experimentEnabled) return null;
    // b: both desktop and mobile — no device guard
    // c: both desktop and mobile, compact layout — no device guard
    const badgeSchema = {
        identifier: HEALTH_LEAD_CAPTURE_ID,
        stateKey: 'vertical.quote',
        field: {
            type: 'badgegroup',
            UIComponent: 'badge_primary_small_rounded',
            metaType: 'checkbox',
            value: 'Y',
            default: 'N',
            label: badgeLabel,
            icon
        }
    };

    const checkboxSchema = {
        ...badgeSchema,
        field: {
            ...badgeSchema.field,
            UIComponent: 'checkbox_small_rounded'
        }
    };

    if (variant === 'c') {
        const variantCCheckboxSchema = {
            identifier: HEALTH_LEAD_CAPTURE_ID,
            stateKey: 'vertical.quote',
            field: {
                type: 'checkbox',
                UIComponent: 'checkbox_medium_rounded',
                labelEl: <Text innerHTML={badgeLabel} />,
                value: 'Y',
                default: 'N'
            }
        };
        return (
            <div className={styles.variant_c_container}>
                <p className={styles.variant_c_title}>{title}</p>
                <div className={styles.variant_c_checkbox}>
                    <Checkbox schema={variantCCheckboxSchema} dispatch={dispatch} value={value} />
                </div>
                <div className={styles.variant_c_content}>
                    <Text innerHTML={content} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div display="flex">
                <h2>{title}</h2>
                <hr />
            </div>
            <SimpleFlexbox display="flex">
                <div>
                    <Badge schema={badgeSchema} checkboxSchema={checkboxSchema} value={value} dispatch={dispatch} />
                </div>
                <div className={styles.text}>
                    <Text innerHTML={content} />
                </div>
            </SimpleFlexbox>
        </div>
    );
};

_CrossSellWidget.propTypes = {
    title: PropTypes.string.isRequired,
    badgeLabel: PropTypes.string.isRequired,
    icon: PropTypes.string,
    content: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['b', 'c']),
    serverConfig: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
    value: quoteFormSelector(state).getIn(HEALTH_LEAD_CAPTURE_ID.split('.')) || 'N',
    serverConfig: state.get('serverConfig')
});

const CrossSellWidget = connect(mapStateToProps)(_CrossSellWidget);

export { _CrossSellWidget, CrossSellWidget };
